/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import "../styles/Contact.css";
import usePageMeta from "../hooks/usePageMeta";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const loadRecaptchaV3 = (siteKey: string) =>
  new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.execute) return resolve();

    const selector = `script[data-recaptcha="v3"][data-sitekey="${siteKey}"]`;
    const existing = document.querySelector(selector);
    if (existing) {
      const onLoad = () => resolve();
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("reCAPTCHA failed to load.")),
        {
          once: true,
        }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = "v3";
    script.dataset.sitekey = siteKey;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("reCAPTCHA failed to load."));
    document.head.appendChild(script);
  });

const ContactSalesPage = () => {
  usePageMeta({
    title: "Contact | PabSync",
    description: "Get in touch with the PabSync team.",
    path: "/contact",
  });

  const webappUrl =
    process.env.REACT_APP_PABSYNC_WEBAPP_URL || "https://app.pabsync.com";

  // API endpoint to receive the contact form.
  // Default uses the existing API envs used by src/Service.ts.
  const apiBase = process.env.REACT_APP_API_BASE || "";
  const apiVersion = process.env.REACT_APP_API_VERSION || "v1";
  const defaultContactUrl = apiBase
    ? `${apiBase}/${apiVersion}/public/contact`
    : "";
  const contactApiUrl =
    process.env.REACT_APP_CONTACT_API_URL || defaultContactUrl;

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);

  const [recaptchaError, setRecaptchaError] = useState<string>("");

  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => {
    if (!privacyOk) return false;
    if (!fullName.trim() || !email.trim() || !message.trim()) return false;
    if (!siteKey) return false;
    return true;
  }, [privacyOk, fullName, email, message, siteKey]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!siteKey) return;

      try {
        await loadRecaptchaV3(siteKey);
        if (cancelled) return;

        if (!window.grecaptcha?.ready || !window.grecaptcha?.execute) {
          throw new Error("reCAPTCHA not available.");
        }

        // Ensure grecaptcha is ready. Token is fetched on submit.
        window.grecaptcha.ready(() => {
          /* noop */
        });
      } catch (e: any) {
        setRecaptchaError(e?.message || "reCAPTCHA failed to load.");
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!siteKey) {
      setSubmitError(
        "Contact form is not configured: missing reCAPTCHA site key."
      );
      return;
    }

    if (!window.grecaptcha?.execute || !window.grecaptcha?.ready) {
      setSubmitError("reCAPTCHA is not ready yet. Please try again.");
      return;
    }

    setSubmitting(true);
    try {
      if (!contactApiUrl) {
        setSubmitError(
          "Contact form is not configured: missing REACT_APP_API_BASE (or REACT_APP_CONTACT_API_URL override)."
        );
        return;
      }

      // reCAPTCHA v3 token (no widget). Fetch per-submit.
      const recaptchaToken = await window.grecaptcha.execute(siteKey, {
        action: "contact",
      });

      const res = await fetch(contactApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName,
          email,
          message,
          recaptchaToken,
          recaptchaAction: "contact",
          source: "Website",
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to send. Please try again in a moment.");
      }

      setSubmitSuccess("Message sent. We’ll get back to you soon.");
      setFullName("");
      setEmail("");
      setMessage("");
      setPrivacyOk(false);
    } catch (err: any) {
      setSubmitError(err?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-card">
          <section className="contact-side contact-side--muted">
            <h1 className="contact-title">Contact us</h1>
            <p className="contact-subtitle">
              Send a message and we’ll route it to the right team. For product
              support and billing changes, use the self-serve pages in the app.
            </p>

            <ul className="contact-links">
              <li>
                <a className="contact-link" href={webappUrl}>
                  <span>Help Center</span>
                  <span className="contact-link__meta">Support</span>
                </a>
              </li>
              <li>
                <a className="contact-link" href={webappUrl}>
                  <span>Billing</span>
                  <span className="contact-link__meta">
                    Manage subscription
                  </span>
                </a>
              </li>
              <li>
                <a className="contact-link" href={webappUrl}>
                  <span>Account</span>
                  <span className="contact-link__meta">Settings</span>
                </a>
              </li>
            </ul>

            <p className="helper" style={{ marginTop: 16 }}>
              Typical response time: 1–2 business days.
            </p>
          </section>

          <section className="contact-side">
            <form className="contact-form" onSubmit={onSubmit}>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="fullName">Full name</label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    placeholder="Jane Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="jane@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us what you’re trying to accomplish and any relevant details."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
                <div className="helper">
                  Please don’t include passwords or sensitive information.
                </div>
              </div>

              <div className="privacy">
                <input
                  id="privacyOk"
                  type="checkbox"
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                  required
                />
                <label htmlFor="privacyOk" className="helper">
                  I agree to the{" "}
                  <a href="/privacy" style={{ fontWeight: 800 }}>
                    Privacy Statement
                  </a>
                  .
                </label>
              </div>

              <div className="recaptcha">
                {!siteKey ? (
                  <div className="error">
                    Missing reCAPTCHA site key. Set{" "}
                    <code>REACT_APP_RECAPTCHA_SITE_KEY</code>.
                  </div>
                ) : null}
                {recaptchaError ? (
                  <div className="error" style={{ marginTop: 10 }}>
                    {recaptchaError}
                  </div>
                ) : null}
                <div className="notice" style={{ marginTop: 10 }}>
                  Protected by reCAPTCHA — Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </div>
              </div>

              {submitError ? <div className="error">{submitError}</div> : null}
              {submitSuccess ? (
                <div className="success">{submitSuccess}</div>
              ) : null}

              <div className="actions">
                <button
                  className="primary-btn"
                  type="submit"
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <div className="notice">
                  Protected by reCAPTCHA. This form requires a server endpoint
                  to verify the token.
                </div>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContactSalesPage;
